import { Button, Group } from "@mantine/core";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useCallback } from "react";
import { Cards } from "@/src/routes/desafios-relatorio-visao-geral/DesafiosRelatorioVisaoGeralPage";
import { textMapDesafios } from "@/src/routes/gestao-inovacao-page/ExportIdeias";
import { TDesafioProcessado } from "../hooks/useDesafiosRelatorio";

const DEZ = 10;
const CEM_PORCENTO = 100;
const DOIS = 2;

interface ExportExcelProperties {
  cards: Cards;
  empresa: TEmpresa | undefined;
  dadosFiltrados: TDesafioProcessado[];
  isLoading: boolean;
}

const ExportExcel = ({ cards, empresa, dadosFiltrados, isLoading }: ExportExcelProperties) => {
  const handleExport = useCallback(async () => {
    if (!empresa?.id || !dadosFiltrados) {
      console.error("Dados da empresa ou para exportação não encontrados.");
      return;
    }

    const workbook = new ExcelJS.Workbook();

    const desafiosExcelData = dadosFiltrados.map((document_) => [
      document_.titulo,
      document_.propositorNome,
      document_.descricao,
      document_.objetivo,
      document_.propositorEmail,
      document_.createdAt,
      textMapDesafios[document_.status],
      document_.moedas > 0 ? document_.moedas : "",
      document_.primeiraImersao || "",
      document_.ultimaImersao || ""
    ]);

    exportaDesafios(workbook, desafiosExcelData as (string | number | Date | undefined)[][]);
    exportaCards(workbook, cards);

    try {
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), `relatorio_desafios.xlsx`);
    } catch (error) {
      console.error("Erro ao gerar a planilha:", error);
    }
  }, [empresa, cards, dadosFiltrados]);

  const onExportClick = useCallback(() => {
    void handleExport().catch((error) => {
      console.error("Falha ao exportar:", error);
    });
  }, [handleExport]);

  return (
    <Group justify={"flex-end"}>
      <Button variant="filled" onClick={onExportClick} loading={isLoading} disabled={dadosFiltrados.length === 0}>
        Exportar
      </Button>
    </Group>
  );
};
export default ExportExcel;

const formatarValor = (numero: number) => {
  if (typeof numero !== "number" || Number.isNaN(numero)) return "0.0%";
  return `${Math.round((numero + Number.EPSILON) * DEZ) / DEZ}%`;
};

const exportaCards = (workbook: ExcelJS.Workbook, cards: Cards): void => {
  if (!cards) return;
  const columns = ["Desafios", "Comentários", "Usuários"];
  const worksheet = workbook.addWorksheet("Resumo");
  const headerRow = worksheet.addRow(columns);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });
  const unicos = new Set([...cards.desafios.participantes, ...cards.comentarios.participantes]).size;

  worksheet.addRow([cards.desafios.total, cards.comentarios.total, cards.colaboradoresAtivos]);
  worksheet.addRow([cards.desafios.participantes.size, cards.comentarios.participantes.size, unicos]);

  worksheet.addRow([
    formatarValor(cards.desafios.porcentagem),
    formatarValor(cards.comentarios.porcentagem),
    formatarValor(cards.colaboradoresAtivos > 0 ? (unicos * CEM_PORCENTO) / cards.colaboradoresAtivos : 0)
  ]);
  calculateAndSetColumnWidths(worksheet, columns);
};

const exportaDesafios = (workbook: ExcelJS.Workbook, csvData: (string | number | Date | undefined)[][]) => {
  const columns = [
    "Título",
    "Autor(es)",
    "Descrição",
    "Objetivo",
    "Email Autor(es)",
    "Data de Criação",
    "Status",
    "Moedas",
    "Primeira Imersão",
    "Última Imersão"
  ];
  const worksheet = workbook.addWorksheet("Desafios");
  const headerRow = worksheet.addRow(columns);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });
  for (const rowData of csvData) {
    worksheet.addRow(rowData);
  }
  calculateAndSetColumnWidths(worksheet, columns);
};

const calculateAndSetColumnWidths = (worksheet: ExcelJS.Worksheet, columns: string[]) => {
  const columnWidths = columns.map((col, index) => {
    let maxLength = col.length;
    worksheet.getColumn(index + 1).eachCell({ includeEmpty: true }, (cell) => {
      const cellValue = cell.value ? String(cell.value) : "";
      maxLength = Math.max(maxLength, cellValue.length);
    });
    return maxLength < DEZ ? DEZ : maxLength + DOIS;
  });
  for (const [index, column] of worksheet.columns.entries()) {
    // eslint-disable-next-line security/detect-object-injection
    column.width = columnWidths[index];
  }
};
